import type { AccountType } from '../banking/BankAccount';
import { type ImAccount } from '../investment-management/accounts';

/**
 * An enum defined in Postgres for the official holistic categorization of accounts.
 * This should not be incidentally confused with the {@link HolisticSummaryCategory} type,
 * which is a client-side type for Holistic frontend functionality. The underlying string
 * values in the enums are not equal, so we must intentionally use this HolisticCategory
 * enum or the HolisticSummaryCategory type.
 */
export enum HolisticCategory {
    SAVE = 'SAVE',
    SPEND = 'SPEND',
    INVEST = 'INVEST',
    BORROW = 'BORROW',
}

export enum SpendAccountCategory {
    CASH = 'cash',
    CREDIT = 'credit',
}

export enum AccountCategory {
    SPEND = 'spend',
    SAVE = 'save',
    INVEST = 'invest',
    BORROW = 'borrow',
    OTHER = 'other',
    UNKNOWN = 'unknown',
}

export enum Provider {
    QUILTT = 'Quiltt',
    SYNCTERA = 'Synctera',
    ORION = 'Orion',
    MANUAL = 'Manual',
    SPINWHEEL = 'Spinwheel',
    APEX = 'Apex',
}

export enum LinkState {
    CONNECTING = 'connecting',
    AWAITING_TOKEN_EXCHANGE = 'awaiting_token_exchange',
    CONNECTED = 'connected',
    DELETED = 'deleted',
    ERROR = 'error',
    UNKNOWN = 'unknown',
}

// TODO Make a shared Card type in types package
// This is just the minimal set to support card data in Grove
export interface HolisticCard {
    form: string;
    cardStatus: string;
}

export interface AccountSummary {
    id: string;
    institutionName: string;
    name: string;
    category: AccountCategory;
    provider: Provider;
    accountNumberMasked?: string;
    logo?: string; // Either a base64 encoded image for external institutions, or <icon>:<color> for custom Fruitful logos
    type?: string; // Potentially user-overridden value for account type
    rawType?: string; // Unaltered account type value from the provider
    currentBalanceCents?: number;
    lastUpdated?: string;
    linkState?: LinkState;
    connectionId?: string; // Quiltt
    needsRecategorization?: boolean; // Flag account in an error state so user can set category manually
    isLastAccountOnLink?: boolean; // Flag account is last on the link, if this is removed we will delete the link
    userId?: string;
    bankAccountId?: string; // bank_accounts.id for synctera accounts only
    fruitfulAccountType?: AccountType;
    isAutopayEnabled?: boolean; // Synctera accounts
    cards?: HolisticCard[]; // Synctera accounts
    primaryOwnerUserId?: string; // only populated for Fruitful depository accounts
    jointOwnerUserId?: string; // only populated for Fruitful depository accounts
    description?: string;
    isHidden?: boolean;
    minimumPayment?: number;
    interestRate?: number;
    // Only for category=SPEND accounts
    // Added for convenience on client side for rendering
    spendAccountCategory?: SpendAccountCategory;
    entireBalancePaidMonthly?: boolean; // Only for credit accounts
    // Spinwheel accounts
    accountRating?: string;
    apexPortfolio?: string;
    apexRiskLevel?: string;

    // TODO Temporary update to this type, remove when backend filters out non-OPEN
    //  Apex accounts
    //  @see MembershipAccountsInfo TODO around line 160
    apexAccount?: ImAccount;
}
